// src/stores/authStore.js

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import http from '../services/http'
import cookieService from '../services/cookie'
import notification from '../services/notification'
import { USER_ROLES } from '../services/config'

const useAuthStore = create(
    persist(
        (set, get) => ({
            user: null,
            isLoading: false,
            error: null,
            isInitialized: false,
            // Presente solo mentre un super admin sta impersonando un'agenzia
            // per fare assistenza: contiene token/user dell'admin per poter
            // tornare al pannello admin senza dover rifare login.
            impersonation: null,

            login: async (email, password, rememberMe = false, turnstileToken = null) => {
                set({ isLoading: true, error: null })

                try {
                    const response = await http.post('/api/login', { email, password, turnstile_token: turnstileToken })
                    const { token, user } = response.data

                    if (token) {
                        const days = rememberMe ? 30 : 7
                        cookieService.setToken(token, days)
                    }

                    set({
                        user,
                        isLoading: false,
                        error: null,
                        isInitialized: true,
                    })

                    notification.success(`Benvenuto ${user.business_name || user.email}`)
                    return { success: true, user }

                } catch (error) {
                    const errorMsg = error.response?.data?.error || 'Login fallito'
                    set({
                        error: errorMsg,
                        isLoading: false,
                        user: null,
                    })
                    notification.error(errorMsg)
                    return { success: false, error: errorMsg }
                }
            },

            // Super admin: entra come una specifica agenzia per fare assistenza,
            // senza bisogno della sua password. Salva la sessione admin corrente
            // per poterci tornare con stopImpersonating().
            impersonateAgency: async (agencyId) => {
                const currentToken = cookieService.getToken()
                const currentUser = get().user

                try {
                    const response = await http.post(`/api/admin/agencies/${agencyId}/impersonate`)
                    const { token, user } = response.data

                    cookieService.setToken(token)
                    set({
                        user,
                        impersonation: { adminToken: currentToken, adminUser: currentUser },
                        error: null,
                    })

                    notification.success(`Stai visualizzando come ${user.business_name || user.email}`)
                    return { success: true, user }
                } catch (error) {
                    const errorMsg = error.response?.data?.error || 'Impossibile accedere come questa agenzia'
                    notification.error(errorMsg)
                    return { success: false, error: errorMsg }
                }
            },

            // Torna alla sessione admin salvata prima di impersonare.
            stopImpersonating: () => {
                const { impersonation } = get()
                if (!impersonation) return false

                cookieService.setToken(impersonation.adminToken)
                set({
                    user: impersonation.adminUser,
                    impersonation: null,
                })
                notification.info('Sei tornato al pannello admin')
                return true
            },

            logout: async (redirectTo = '/agency/login') => {
                cookieService.removeToken()
                set({
                    user: null,
                    error: null,
                    isLoading: false,
                    impersonation: null,
                })
                notification.info('Logout effettuato')

                if (redirectTo) {
                    window.location.href = redirectTo
                }
            },

            checkAuth: async () => {
                const token = cookieService.getToken()
                if (!token) {
                    set({ user: null, isInitialized: true })
                    return false
                }

                set({ isLoading: true })

                try {
                    const response = await http.get('/api/verify')
                    set({
                        user: response.data.user,
                        isLoading: false,
                        isInitialized: true,
                    })
                    return true

                } catch (_error) {  // <- Rinomina in _error per evitare warning
                    console.debug(_error)
                    cookieService.removeToken()
                    set({
                        user: null,
                        isLoading: false,
                        isInitialized: true,
                        error: null,
                    })
                    return false
                }
            },


            updateUser: (updates) => {
                set((state) => ({
                    user: state.user ? { ...state.user, ...updates } : null
                }))
            },

            clearError: () => set({ error: null }),

            isAuthenticated: () => {
                return cookieService.isAuthenticated() && get().user !== null
            },

            isAgency: () => {
                const { user } = get()
                return user?.role === USER_ROLES.AGENCY
            },

            isSuperAdmin: () => {
                const { user } = get()
                return user?.role === USER_ROLES.SUPER_ADMIN
            },

            getToken: () => cookieService.getToken(),
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                user: state.user,
                isInitialized: state.isInitialized,
                impersonation: state.impersonation,
            }),
        }
    )
)

export default useAuthStore